<template>
  <div class="err-logs">
    <APageHead title="에러 로그 관리" sub="시스템 에러를 실시간으로 모니터링하고 관리하세요.">
      <template #actions>
        <ABtn kind="ghost" icon="refresh" @click="fetchDashboard">새로고침</ABtn>
        <ABtn kind="danger" icon="delete_sweep" @click="showCleanupDialog">로그 정리</ABtn>
      </template>
    </APageHead>

    <div v-if="dashboard" class="err-logs__stats">
      <ACard>
        <div class="err-logs__stat-row">
          <AStat label="총 에러" :value="String(dashboard.error_logs?.total || 0)" />
          <div class="err-logs__stat-icon err-logs__stat-icon--warn"><i class="material-icons-round">report</i></div>
        </div>
      </ACard>
      <ACard>
        <div class="err-logs__stat-row">
          <AStat label="심각 에러" :value="String(dashboard.error_logs?.critical_count || 0)" />
          <div class="err-logs__stat-icon err-logs__stat-icon--err"><i class="material-icons-round">warning</i></div>
        </div>
      </ACard>
      <ACard>
        <div class="err-logs__stat-row">
          <AStat label="최근 업데이트" :value="formatTime(dashboard.timestamp)" />
          <div class="err-logs__stat-icon err-logs__stat-icon--info"><i class="material-icons-round">access_time</i></div>
        </div>
      </ACard>
    </div>

    <div
      v-if="dashboard?.error_logs?.recent_errors?.length > 0"
      class="err-logs__recent-alert"
    >
      <div class="err-logs__recent-head">
        <i class="material-icons-round">notifications_active</i>
        <strong>최근 에러</strong>
      </div>
      <div
        v-for="(error, index) in dashboard.error_logs.recent_errors.slice(0, 3)"
        :key="index"
        class="err-logs__recent-item"
      >
        <div class="err-logs__recent-meta">
          <APill kind="err">{{ error.level }}</APill>
          <span class="err-logs__num">{{ formatDateTime(error.timestamp) }}</span>
        </div>
        <div class="err-logs__recent-msg">{{ error.message }}</div>
      </div>
    </div>

    <ACard>
      <div class="err-logs__filters">
        <div class="p-field err-logs__filter err-logs__filter--sm">
          <label class="p-field__label">레벨</label>
          <select v-model="filters.level" class="p-input">
            <option value="">전체</option>
            <option value="ERROR">ERROR</option>
            <option value="CRITICAL">CRITICAL</option>
          </select>
        </div>
        <div class="p-field err-logs__filter err-logs__filter--md">
          <label class="p-field__label">모듈</label>
          <input v-model="filters.module" class="p-input" placeholder="모듈명" />
        </div>
        <div class="p-field err-logs__filter err-logs__filter--md">
          <label class="p-field__label">시작일</label>
          <input v-model="filters.start_date" type="date" class="p-input" />
        </div>
        <div class="p-field err-logs__filter err-logs__filter--md">
          <label class="p-field__label">종료일</label>
          <input v-model="filters.end_date" type="date" class="p-input" />
        </div>
        <div class="p-field err-logs__filter err-logs__filter--grow">
          <label class="p-field__label">검색</label>
          <input v-model="filters.search_text" class="p-input" placeholder="메시지 검색" @keyup.enter="resetPage" />
        </div>
        <div class="p-field err-logs__filter err-logs__filter--sm">
          <label class="p-field__label">개수</label>
          <select v-model.number="pageSize" class="p-input" @change="resetPage">
            <option :value="10">10개</option>
            <option :value="20">20개</option>
            <option :value="50">50개</option>
            <option :value="100">100개</option>
            <option :value="200">200개</option>
          </select>
        </div>
        <div class="err-logs__filter-btn">
          <ABtn kind="yellow" icon="search" @click="resetPage">검색</ABtn>
        </div>
      </div>
    </ACard>

    <ACard body-flush>
      <AEmpty
        v-if="!loading && logs.length === 0"
        title="에러 로그가 없습니다"
        sub="시스템이 정상적으로 작동중입니다."
      />

      <ATable
        v-else
        :columns="cols"
        :rows="logs"
        row-key="id"
      >
        <template #cell:timestamp="{ row }">
          <span class="err-logs__num">{{ formatDateTime(row.timestamp) }}</span>
        </template>
        <template #cell:domain="{ row }">{{ row.domain || '-' }}</template>
        <template #cell:level="{ row }">
          <APill :kind="row.level === 'CRITICAL' ? 'err' : 'warn'">{{ row.level }}</APill>
        </template>
        <template #cell:module="{ row }">{{ row.module || '-' }}</template>
        <template #cell:function="{ row }">{{ row.function || '-' }}</template>
        <template #cell:message="{ row }">
          <div class="err-logs__msg" :title="row.message">{{ row.message }}</div>
        </template>
        <template #cell:actions="{ row }">
          <div class="err-logs__row-actions">
            <ABtn kind="ghost" size="sm" icon="visibility" icon-only @click="showDetails(row)" />
            <ABtn kind="danger" size="sm" icon="delete" icon-only @click="deleteLog(row.id)" />
          </div>
        </template>
      </ATable>

      <div v-if="total > 0" class="err-logs__pager-row">
        <div class="err-logs__pager-total">
          총 <strong>{{ total.toLocaleString() }}</strong>건 · {{ currentPage }} / {{ totalPages }} 페이지
        </div>
        <div v-if="totalPages > 1" class="err-logs__pager">
          <ABtn
            kind="ghost"
            size="sm"
            icon="chevron_left"
            icon-only
            :disabled="currentPage === 1"
            @click="changePage(currentPage - 1)"
          />
          <button
            v-for="page in displayPages"
            :key="page"
            type="button"
            class="err-logs__pager-num"
            :class="{ 'is-active': page === currentPage }"
            @click="changePage(page)"
          >{{ page }}</button>
          <ABtn
            kind="ghost"
            size="sm"
            icon="chevron_right"
            icon-only
            :disabled="currentPage === totalPages"
            @click="changePage(currentPage + 1)"
          />
        </div>
      </div>
    </ACard>

    <!-- 상세 모달 -->
    <AModal v-model="detailOpen" title="에러 로그 상세 정보" size="xl">
      <div v-if="selectedLog" class="err-logs__detail">
        <div class="err-logs__detail-grid">
          <div>
            <h6 class="err-logs__detail-h">기본 정보</h6>
            <table class="err-logs__detail-tbl">
              <tr><th>ID</th><td>{{ selectedLog.id }}</td></tr>
              <tr><th>발생 시간</th><td>{{ formatDateTime(selectedLog.timestamp) }}</td></tr>
              <tr>
                <th>레벨</th>
                <td><APill :kind="selectedLog.level === 'CRITICAL' ? 'err' : 'warn'">{{ selectedLog.level }}</APill></td>
              </tr>
              <tr><th>로거</th><td>{{ selectedLog.logger_name || '-' }}</td></tr>
            </table>
          </div>
          <div>
            <h6 class="err-logs__detail-h">에러 정보</h6>
            <table class="err-logs__detail-tbl">
              <tr><th>타입</th><td>{{ selectedLog.exception_type || '-' }}</td></tr>
              <tr><th>값</th><td>{{ selectedLog.exception_value || '-' }}</td></tr>
              <tr><th>메시지</th><td>{{ selectedLog.message }}</td></tr>
            </table>
          </div>
        </div>

        <h6 class="err-logs__detail-h">위치 정보</h6>
        <table class="err-logs__detail-tbl">
          <tr><th>파일</th><td>{{ selectedLog.pathname || '-' }}</td></tr>
          <tr><th>모듈</th><td>{{ selectedLog.module || '-' }}</td></tr>
          <tr><th>함수</th><td>{{ selectedLog.function || '-' }} (Line: {{ selectedLog.line_number || '-' }})</td></tr>
        </table>

        <div v-if="selectedLog.traceback && selectedLog.traceback.length > 0">
          <h6 class="err-logs__detail-h">스택 트레이스</h6>
          <pre class="err-logs__traceback">{{ formatTraceback(selectedLog.traceback) }}</pre>
        </div>

        <div v-if="selectedLog.extra_data && Object.keys(selectedLog.extra_data).length > 0">
          <h6 class="err-logs__detail-h">추가 정보</h6>
          <table class="err-logs__detail-tbl">
            <tr v-for="(value, key) in selectedLog.extra_data" :key="key">
              <th>{{ key }}</th><td>{{ value }}</td>
            </tr>
          </table>
        </div>
      </div>
      <template #foot>
        <ABtn kind="ghost" @click="detailOpen = false">닫기</ABtn>
        <ABtn v-if="selectedLog" kind="danger-fill" icon="delete" @click="deleteLog(selectedLog.id)">삭제</ABtn>
      </template>
    </AModal>

    <!-- 정리 모달 -->
    <AModal v-model="cleanupOpen" title="오래된 에러 로그 정리" size="sm">
      <p class="err-logs__cleanup-d">며칠 이전의 에러 로그를 삭제하시겠습니까?</p>
      <div class="p-field">
        <label class="p-field__label">보관 기간 (일)</label>
        <select v-model="cleanupDays" class="p-input">
          <option value="7">7일 이전</option>
          <option value="14">14일 이전</option>
          <option value="30">30일 이전</option>
          <option value="60">60일 이전</option>
          <option value="90">90일 이전</option>
        </select>
      </div>
      <div class="err-logs__alert err-logs__alert--warn">
        <i class="material-icons-round">warning</i>
        선택한 기간 이전의 모든 에러 로그가 영구적으로 삭제됩니다. 되돌릴 수 없습니다.
      </div>
      <template #foot>
        <ABtn kind="ghost" @click="cleanupOpen = false">취소</ABtn>
        <ABtn kind="danger-fill" icon="delete" @click="performCleanup">삭제 실행</ABtn>
      </template>
    </AModal>
  </div>
</template>

<script>
import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import AStat from "@/components/Pineapple/AStat.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AModal from "@/components/Pineapple/AModal.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";

export default {
  name: "SystemLogsErrors",
  components: { APageHead, ACard, ABtn, APill, AStat, ATable, AModal, AEmpty },
  data() {
    return {
      logs: [],
      dashboard: null,
      loading: false,
      filters: {
        level: '',
        module: '',
        start_date: '',
        end_date: '',
        search_text: '',
        page: 1,
        page_size: 50,
      },
      total: 0,
      currentPage: 1,
      pageSize: 20,
      totalPages: 1,
      selectedLog: null,
      cleanupDays: 30,
      detailOpen: false,
      cleanupOpen: false,
      cols: [
        { key: 'timestamp', label: '시간',   width: 170 },
        { key: 'domain',    label: '도메인', width: 100 },
        { key: 'level',     label: '레벨',   width: 100, align: 'center' },
        { key: 'module',    label: '모듈' },
        { key: 'function',  label: '함수' },
        { key: 'message',   label: '메시지' },
        { key: 'actions',   label: '작업', width: 90, align: 'center' },
      ],
    };
  },
  computed: {
    displayPages() {
      const pages = [];
      const maxDisplay = 5;
      const half = Math.floor(maxDisplay / 2);
      let start = Math.max(1, this.currentPage - half);
      let end = Math.min(this.totalPages, start + maxDisplay - 1);
      if (end - start < maxDisplay - 1) start = Math.max(1, end - maxDisplay + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },
  },
  mounted() {
    this.initializeDates();
    this.fetchDashboard();
    this.fetchLogs();
  },
  methods: {
    initializeDates() {
      const today = new Date();
      const oneWeekAgo = new Date(today);
      oneWeekAgo.setDate(today.getDate() - 7);
      this.filters.end_date = this.formatDate(today);
      this.filters.start_date = this.formatDate(oneWeekAgo);
    },

    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    async fetchDashboard() {
      try {
        const response = await this.$axios({
          method: "get",
          url: "/system/logs/dashboard",
          params: { days: 7 },
        });
        this.dashboard = response.data;
      } catch (error) {
        console.error("대시보드 조회 실패:", error);
      }
    },

    async fetchLogs() {
      this.loading = true;
      try {
        const params = { page: this.currentPage, page_size: this.pageSize };
        if (this.filters.level) params.level = this.filters.level;
        if (this.filters.module) params.module = this.filters.module;
        if (this.filters.search_text) params.search_text = this.filters.search_text;
        if (this.filters.start_date) params.start_date = `${this.filters.start_date}T00:00:00`;
        if (this.filters.end_date) params.end_date = `${this.filters.end_date}T23:59:59`;

        const response = await this.$axios({
          method: "get",
          url: "/system/logs/errors",
          params,
          loadingBar: true,
        });

        this.logs = response.data.logs || [];
        this.total = response.data.total || 0;
        this.totalPages = Math.ceil(this.total / this.pageSize);
      } catch (error) {
        console.error("로그 조회 실패:", error);
        this.$notifyVue({ type: "error", title: "에러 로그를 불러오는데 실패했습니다." });
        this.logs = [];
      } finally {
        this.loading = false;
      }
    },

    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      this.fetchLogs();
    },

    resetPage() {
      this.currentPage = 1;
      this.fetchLogs();
    },

    async showDetails(log) {
      try {
        const response = await this.$axios({
          method: "get",
          url: `/system/logs/errors/${log.id}`,
          loadingBar: true,
        });
        this.selectedLog = response.data;
        this.detailOpen = true;
      } catch (error) {
        console.error("로그 상세 조회 실패:", error);
        this.$notifyVue({ type: "error", title: "로그 상세 정보를 불러오는데 실패했습니다." });
      }
    },

    async deleteLog(logId) {
      if (!confirm("이 로그를 삭제하시겠습니까?")) return;
      try {
        await this.$axios({
          method: "delete",
          url: `/system/logs/errors/${logId}`,
          loadingBar: true,
        });
        this.$notifyVue({ type: "success", title: "로그가 삭제되었습니다." });
        this.detailOpen = false;
        this.fetchLogs();
      } catch (error) {
        console.error("로그 삭제 실패:", error);
        this.$notifyVue({ type: "error", title: "로그 삭제에 실패했습니다." });
      }
    },

    showCleanupDialog() {
      this.cleanupOpen = true;
    },

    async performCleanup() {
      try {
        const response = await this.$axios({
          method: "delete",
          url: "/system/logs/errors/cleanup/old-logs",
          params: { days: this.cleanupDays },
          loadingBar: true,
        });
        this.$notifyVue({
          type: "success",
          title: `${response.data.deleted_count}개의 에러 로그가 삭제되었습니다.`,
        });
        this.cleanupOpen = false;
        this.fetchDashboard();
        this.fetchLogs();
      } catch (error) {
        console.error("로그 정리 실패:", error);
        this.$notifyVue({ type: "error", title: "로그 정리에 실패했습니다." });
      }
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },

    formatTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    },

    formatTraceback(traceback) {
      if (!traceback || !Array.isArray(traceback)) return '';
      return traceback.join('\n');
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.err-logs {
  display: flex;
  flex-direction: column;
  gap: 18px;

  &__stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;

    @media (max-width: 768px) { grid-template-columns: 1fr; }
  }

  &__stat-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }

  &__stat-icon {
    width: 44px;
    height: 44px;
    border-radius: $p-radius-md;
    display: grid;
    place-items: center;

    > i { font-size: 22px; }

    &--warn { background: $p-warn-soft; color: $p-warn; }
    &--err  { background: $p-err-soft;  color: $p-err; }
    &--info { background: $p-info-soft; color: $p-info; }
  }

  &__recent-alert {
    background: $p-err-soft;
    border-left: 3px solid $p-err;
    padding: 14px 18px;
    border-radius: $p-radius-md;
  }

  &__recent-head {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 10px;
    color: $p-err;
    font-size: 13px;

    > i { font-size: 18px; }
  }

  &__recent-item {
    padding: 8px 0;
    border-top: 1px solid rgba(196, 74, 58, 0.15);

    &:first-of-type { border-top: 0; padding-top: 0; }
  }

  &__recent-meta {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__recent-msg {
    margin-top: 4px;
    font-size: 12px;
    color: $p-ink2;
    font-family: $p-font-mono;
  }

  &__filters {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 10px;
  }

  &__filter {
    flex: 0 0 auto;

    &--sm { width: 110px; }
    &--md { width: 150px; }
    &--grow {
      flex: 1 1 200px;
      min-width: 160px;
    }
  }

  &__filter-btn {
    display: flex;
    align-items: flex-end;
    padding-bottom: 1px;
  }

  &__num {
    font-family: $p-font-mono;
    font-variant-numeric: tabular-nums;
    font-size: 11px;
  }

  &__msg {
    max-width: 380px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    color: $p-ink-mid;
  }

  &__row-actions {
    display: inline-flex;
    gap: 4px;
  }

  &__pager-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    border-top: 1px solid $p-line;
    flex-wrap: wrap;
  }

  &__pager-total {
    font-size: 12px;
    color: $p-ink-mid;
    font-weight: 600;

    > strong {
      color: $p-ink;
      font-family: $p-font-mono;
      font-weight: 800;
    }
  }

  &__pager {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__pager-num {
    min-width: 32px;
    height: 32px;
    padding: 0 8px;
    border-radius: $p-radius;
    border: 1px solid $p-line;
    background: $p-panel;
    color: $p-ink-mid;
    font-family: $p-font-mono;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;

    &:hover {
      background: $p-point-soft;
      color: $p-point-deep;
    }

    &.is-active {
      background: $p-point-deep;
      border-color: $p-point-deep;
      color: $p-panel;
    }
  }

  // 모달 상세
  &__detail {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__detail-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @media (max-width: 768px) { grid-template-columns: 1fr; }
  }

  &__detail-h {
    margin: 0 0 8px;
    font-size: 12px;
    font-weight: 800;
    color: $p-ink2;
  }

  &__detail-tbl {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;

    th, td {
      padding: 8px 12px;
      border: 1px solid $p-line;
      text-align: left;
      word-break: break-word;
    }

    th {
      background: $p-panel-alt;
      color: $p-ink-mid;
      font-weight: 800;
      width: 30%;
    }
  }

  &__traceback {
    margin: 0;
    padding: 14px 16px;
    background: $p-ink;
    color: $p-point;
    border-radius: $p-radius-md;
    font-family: $p-font-mono;
    font-size: 11px;
    line-height: 1.5;
    white-space: pre-wrap;
    max-height: 320px;
    overflow-y: auto;
  }

  &__alert {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 14px;
    border-radius: $p-radius-md;
    font-size: 12px;
    margin-top: 12px;

    > i { font-size: 18px; }

    &--warn { background: $p-warn-soft; border-left: 3px solid $p-warn; color: $p-warn; }
  }

  &__cleanup-d {
    margin: 0 0 14px;
    font-size: 13px;
    color: $p-ink;
  }
}
</style>
